<template>
  <v-card-text>
    <document-page-template
      :document="document.document"
      :loading="$loading.isLoading(['getDocument', 'getClientAccess'])"
      :watermarks="
        keepArray(document.participants).map(p => {
          return {
            isStamp: !!p.certificate.extKeyUsages,
            text: `${p.certificate.subjOrg} ${
              !!p.certificate.extKeyUsages ? '' : p.certificate.subjCN
            } ЄДРПОУ/ІПН ${p.certificate.subjEDRPOUCode || '---'}`,
          }
        })
      "
      :token="authToken">
      <template #top-header>
        <div class="d-flex gap-1">
          <v-tooltip text="Завантажити">
            <template #activator="{ props: tooltipProps }">
              <v-menu>
                <template #activator="{ props: menuProps }">
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    v-bind="{ ...tooltipProps, ...menuProps }">
                    <v-icon color="primary" size="large">mdi-upload</v-icon>
                  </v-btn>
                </template>

                <b-action-list
                  :actions="[
                    {
                      title: 'Завантажити архів',
                      action: () => downloadArchive(),
                    },
                    {
                      title: 'Завантажити P7S',
                      action: () => downloadP7S(),
                    },
                    {
                      title: 'Завантажити протокол підписання',
                      action: () => downloadProtocol(),
                    },
                  ]"></b-action-list>
              </v-menu>
            </template>
          </v-tooltip>
        </div>
      </template>
      <template #info>
        <div>
          <div
            v-if="clientAccess?.isSignerDocument && !clientSignatures.length">
            <v-btn class="w-100" @click="signDocument"> Підписати</v-btn>
            <v-divider class="mb-2"></v-divider>
          </div>

          <signature-status :document="document"></signature-status>
        </div>
      </template>
    </document-page-template>
  </v-card-text>
</template>

<script lang="ts">
import { BActionList } from 'best-modules/components'
import SignatureStatus from '@/components/signature/SignatureStatus.vue'
import DocumentPageTemplate from '@/components/document-page-template/Index.vue'
import {
  urlClientAccess,
  urlClientAccessDocument,
  urlClientAccessCreateSign,
  urlClientAccessGetDiiaData,
  urlClientSignatureArchive,
  urlClientSignatureProtocol,
  urlClineAccessRepack,
} from '@/utils/urls'
import axios from '@/plugins/axios/index.js'
import { computed, reactive } from 'vue'
import { handleAsync } from 'best-modules/plugins'
import { useRouter } from 'vue-router'
import { openDialog } from '@/dialog'
import { keepArray, toPath } from 'best-modules/utils'
import { download } from 'best-modules/vue-utils'
import { documentSignatureSingleChannel, socket } from '@/socket.js'
import {
  DiiaRequestPayload,
  SignedDocument,
} from '@/dialog/components/sign-document/types'

export default {
  name: 'Document',
  components: {
    SignatureStatus,
    DocumentPageTemplate,
    BActionList,
  },
  props: {
    authToken: {
      type: String,
      required: true,
    },
  },
  methods: { keepArray, download },
  setup(props) {
    const router = useRouter()

    const clientAccess = reactive({})

    const document = reactive({
      id: null,
      contractor: null,
      participants: null as any,
      documentSigned: null,
    })

    const clientSignatures = computed(() => {
      if (!document.participants) {
        return []
      }
      return document.participants.filter((s: any) => {
        return s.entityTypeId === 5 && !!s.documentSigner
      })
    })

    const getDocument = () => {
      return handleAsync('getDocument', () => {
        return axios({
          url: urlClientAccessDocument(),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
        })
          .then(res => {
            Object.assign(document, res.data)
            window.document.title = res.data.document[0].name
          })
          .catch(err => {
            router.push({ name: 'share-document-access-denied' })

            return err
          })
      })
    }
    const getClientAccess = () => {
      return handleAsync('getClientAccess', () => {
        return axios({
          url: urlClientAccess(),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
        }).then(res => {
          Object.assign(clientAccess, res.data)
        })
      })
    }

    getDocument().then(() => {
      socket.on(documentSignatureSingleChannel, res => {
        if (res.id === document.id) {
          Object.assign(document, res)
        }
      })
    })
    getClientAccess()

    const saveSignatures = async (signatures: SignedDocument[]) => {
      const savedSignatures = []

      for (const signature of signatures) {
        const { data } = await axios.post(
          urlClientAccessCreateSign(),
          {
            documentId: document.id,
            documentSigned: signature.documentSigned,
            signingDate: signature.signingDate,
            fullCertificate: signature.fullCertificate,
          },
          {
            headers: {
              Authorization: `Bearer ${props.authToken}`,
            },
          }
        )

        savedSignatures.push(data)
      }

      return savedSignatures
    }

    const signDocument = () => {
      openDialog({
        name: 'SignDocument',
        dialogData: {
          authToken: props.authToken,
          documents: document.document.map(d => {
            return {
              document: d,
              uploadFilesParams: {
                mainFolder: 'document',
                nameFolder: 'signature',
                prefixFolder: String(document.id),
                isClient: true,
              },
            }
          }),
          diiaRequest: ({ algo, hash }: DiiaRequestPayload) => {
            return axios({
              url: urlClientAccessGetDiiaData(),
              method: 'post',
              headers: {
                Authorization: `Bearer ${props.authToken}`,
              },
              data: { algo, hash },
            })
          },
        },
        params: {
          hideActionButtons: true,
          cb: async signatures => {
            if (Array.isArray(signatures)) {
              const savedSignatures = await saveSignatures(signatures)

              if (!document.participants) {
                document.participants = []
              }
              document.participants.push(...savedSignatures)
            } else {
              Object.assign(document, signatures)
            }
          },
        },
      })
    }

    const downloadProtocol = () => {
      return handleAsync('downloadProtocol', async () => {
        const { data } = await axios({
          url: urlClientSignatureProtocol(),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
        })
        const { data: blob }: { data: Blob } = await axios({
          url: toPath(data.path),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
          responseType: 'blob',
        })
        const url = URL.createObjectURL(blob)

        return download(url, data.path.split('/').at(-1))
      })
    }

    const downloadArchive = () => {
      return handleAsync('downloadArchive', async () => {
        const { data } = await axios({
          url: urlClientSignatureArchive(),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
        })
        const { data: blob }: { data: Blob } = await axios({
          url: toPath(data.path),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
          responseType: 'blob',
        })
        const url = URL.createObjectURL(blob)

        return download(url, data.path.split('/').at(-1))
      })
    }
    const downloadP7S = () => {
      return handleAsync('downloadP7S', async () => {
        const { data } = await axios({
          url: urlClineAccessRepack(),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
        })

        const { data: blob }: { data: Blob } = await axios({
          url: toPath(data.path),
          method: 'get',
          headers: {
            Authorization: `Bearer ${props.authToken}`,
          },
          responseType: 'blob',
        })
        const url = URL.createObjectURL(blob)

        return download(url, data.path.split('/').at(-1))
      })
    }

    return {
      document,
      clientAccess,
      signDocument,
      clientSignatures,
      downloadProtocol,
      downloadArchive,
      downloadP7S,
    }
  },
}
</script>

<style lang="scss" scoped></style>
